/**
 * Sidebar Component - Enhanced with collapsible sections, icons, and badges
 */

import { useState } from 'react'
import {
  X,
  ChevronDown,
  ChevronRight,
  LayoutDashboard,
  Wallet,
  ShieldCheck,
  Crown,
  User,
  Compass,
  MessageSquare,
  Settings,
  Building2,
  FileCheck,
  Handshake,
  DollarSign,
  Users,
  Upload,
  ClipboardList,
  Briefcase,
  LogOut,
} from 'lucide-react'
import { useIsMobile } from '../../hooks/useMediaQuery'

const iconMap = {
  LayoutDashboard,
  Wallet,
  ShieldCheck,
  Crown,
  User,
  Compass,
  MessageSquare,
  Settings,
  Building2,
  FileCheck,
  Handshake,
  DollarSign,
  Users,
  Upload,
  ClipboardList,
  Briefcase,
  LogOut,
}

function SidebarContent({ nav, activeTab, onSelect, isMobile, onLogout }) {
  const [openSections, setOpenSections] = useState(() => {
    const activeSection = nav.find((item) => {
      if (item.children) {
        return item.children.some((child) => child.route === activeTab)
      }
      return item.route === activeTab
    })
    return activeSection ? [activeSection.id] : []
  })

  const toggleSection = (id) => {
    setOpenSections((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]
    )
  }

  const isRouteActive = (route) => activeTab === route

  // ✅ Handle item click - check if it's logout
  const handleItemClick = (item) => {
    if (item.route === 'logout') {
      onLogout?.()
      return
    }
    onSelect(item.route)
  }

  return (
    <nav className="flex-1 py-4 overflow-y-auto">
      {nav.map((item) => {
        const hasChildren = item.children && item.children.length > 0
        const isActive = !hasChildren && isRouteActive(item.route)
        const Icon = iconMap[item.icon] || LayoutDashboard
        const isOpen = openSections.includes(item.id)

        // ✅ Check if this is the logout item
        const isLogout = item.route === 'logout' || item.id === 'logout'

        return (
          <div key={item.id} className="mb-0.5">
            {!hasChildren ? (
              <button
                onClick={() => handleItemClick(item)}
                className={`
                  w-full flex items-center gap-3
                  px-5 py-2.5 text-sm
                  transition-all duration-200
                  ${isLogout ? 'text-rose-600 hover:bg-rose-50' : ''}
                  ${isActive && !isLogout
                    ? 'text-brand font-semibold bg-brand-soft/50'
                    : !isLogout ? 'text-ink hover:bg-neutral-100' : ''
                  }
                `}
              >
                <Icon
                  size={18}
                  className={
                    isLogout ? 'text-rose-600' :
                    isActive ? 'text-brand' : 'text-neutral-400'
                  }
                />
                <span className="flex-1 text-left">{item.label}</span>
                {item.badge && (
                  <span className="px-2 py-0.5 text-[10px] font-semibold bg-brand-soft text-brand rounded-full">
                    {item.badge}
                  </span>
                )}
              </button>
            ) : (
              <div>
                <button
                  onClick={() => toggleSection(item.id)}
                  className={`
                    w-full flex items-center justify-between
                    px-5 py-2.5 text-sm
                    transition-colors duration-200
                    text-ink hover:bg-neutral-100
                  `}
                >
                  <span className="flex items-center gap-3">
                    <Icon size={18} className="text-neutral-400" />
                    <span className="font-semibold">{item.label}</span>
                  </span>
                  <span className="text-neutral-400">
                    {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                  </span>
                </button>

                {isOpen && (
                  <div className="pl-12 pr-5 pt-1 pb-1">
                    {item.children.map((child) => {
                      const isChildActive = isRouteActive(child.route)
                      const ChildIcon = child.icon ? iconMap[child.icon] : null
                      const isChildLogout = child.route === 'logout'

                      return (
                        <button
                          key={child.id}
                          onClick={() => handleItemClick(child)}
                          className={`
                            block w-full text-left
                            py-2 px-3 text-sm
                            rounded-lg
                            transition-all duration-200
                            ${isChildLogout ? 'text-rose-600 hover:bg-rose-50' : ''}
                            ${isChildActive && !isChildLogout
                              ? 'text-brand font-semibold bg-brand-soft/30'
                              : !isChildLogout ? 'text-neutral-500 hover:text-ink hover:bg-neutral-100' : ''
                            }
                          `}
                        >
                          <span className="flex items-center gap-2.5">
                            {ChildIcon && (
                              <ChildIcon size={14} className={
                                isChildLogout ? 'text-rose-600' :
                                isChildActive ? 'text-brand' : 'text-neutral-400'
                              } />
                            )}
                            {child.label}
                          </span>
                        </button>
                      )
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        )
      })}
    </nav>
  )
}

export default function Sidebar({
  nav,
  activeTab,
  onSelect,
  mobileOpen,
  onCloseMobile,
  onLogout,  // ✅ New prop
}) {
  const isMobile = useIsMobile()

  const handleSelect = (route) => {
    onSelect(route)
    if (isMobile) {
      onCloseMobile?.()
    }
  }

  const sidebarContent = (
    <SidebarContent
      nav={nav}
      activeTab={activeTab}
      onSelect={handleSelect}
      isMobile={isMobile}
      onLogout={onLogout}
    />
  )

  // Desktop sidebar
  if (!isMobile) {
    return (
      <aside className="hidden md:flex w-[260px] shrink-0 h-full bg-sidebar border-r border-neutral-200 flex-col">
        {/* Logo */}
        <div className="h-14 shrink-0 flex items-center px-5 border-b border-neutral-200">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 bg-brand rounded-lg flex items-center justify-center">
              <span className="text-white text-sm font-bold">B</span>
            </div>
            <span className="font-bold text-lg text-ink tracking-tight">
              Bizos Suite
            </span>
          </div>
        </div>

        {sidebarContent}
      </aside>
    )
  }

  // Mobile sidebar (overlay)
  if (!mobileOpen) return null

  return (
    <div className="md:hidden fixed inset-0 z-50">
      <div className="absolute inset-0 bg-ink/40" onClick={onCloseMobile} aria-hidden />
      <aside className="absolute left-0 top-0 bottom-0 w-[280px] bg-sidebar border-r border-neutral-200 flex flex-col animate-slide-in">
        <div className="h-14 flex items-center justify-between px-5 border-b border-neutral-200">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 bg-brand rounded-lg flex items-center justify-center">
              <span className="text-white text-sm font-bold">B</span>
            </div>
            <span className="font-bold text-ink tracking-tight">Bizos Suite</span>
          </div>
          <button
            onClick={onCloseMobile}
            className="text-neutral-500 hover:text-ink p-1"
            aria-label="Close menu"
          >
            <X size={20} />
          </button>
        </div>

        {sidebarContent}
      </aside>
    </div>
  )
}